import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { SavedCard, SavedCardsResponse } from '../../types/server-types';
import { CacheKeys } from './cache-configuration';
import { http } from '../http';

/**
 * Cards the user kept at this location. Tokens are issued per gateway
 * merchant and merchants are per location, so the list is always scoped.
 */
export const useSavedCards = (
  locationId: number | undefined,
): UseQueryResult<SavedCardsResponse> => {
  return useQuery({
    queryKey: [CacheKeys.SAVED_CARDS, locationId],
    queryFn: () => http.getSavedCards(locationId as number),
    enabled: !!locationId,
  });
};

export const useDeleteSavedCard = (locationId: number | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => http.deleteSavedCard(id),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [CacheKeys.SAVED_CARDS, locationId],
      }),
  });
};

/** "Mastercard •••• 0008" from what the gateway reports about the card. */
export const formatSavedCard = (card: SavedCard): string => {
  const brand = card.cardType
    ? card.cardType.charAt(0).toUpperCase() + card.cardType.slice(1)
    : 'Card';
  const last4 = card.maskedPan
    ? card.maskedPan.split('-').pop()?.slice(-4)
    : undefined;
  return last4 ? `${brand} •••• ${last4}` : brand;
};
